import React, { useState } from "react";
import api from "@/lib/api";
import { Card, Button, Input } from "@/components/UI";
import { useAuth } from "@/context/AuthContext";
import { Baby, Robot, Trash, LockSimple, LockSimpleOpen, CheckCircle, Scales, CurrencyCircleDollar, ChartBar } from "@phosphor-icons/react";
import { toast } from "sonner";
import { DAY_LABELS, errMsg } from "./helpers";


export function WeeklyCard({ card, fmt, onChanged, onRemoved, onMutate }) {
  const { user } = useAuth();
  const isManager = user?.role === "owner" || user?.role === "manager";
  const mode = card.mode || "manual";
  const isLely = mode === "lely_calm";
  const confirmed = !!card.is_confirmed;
  const [busy, setBusy] = useState(false);

  // Gün girişleri taslağı: manual → count, lely → kg
  const [draft, setDraft] = useState(() =>
    (card.days || []).map(d => (isLely ? String(d.kg ?? 0) : String(d.count ?? 0)))
  );
  const [calfCount, setCalfCount] = useState(String(card.total_calf_count ?? ""));

  const theme = isLely
    ? { bg: "#E4EEE6", border: "#c2d6c8", fg: "#2D4A36", Icon: Robot }
    : { bg: "#FBF1DE", border: "#e7d5ac", fg: "#8B6914", Icon: Baby };

  const saveDay = async (idx) => {
    if (confirmed) return;
    const raw = draft[idx];
    const body = { day_index: idx };
    if (isLely) {
      const kg = Math.max(0, Number(raw) || 0);
      if (kg === (card.days[idx]?.kg ?? 0)) return;
      body.kg = kg;
    } else {
      let count = Math.max(0, Math.floor(Number(raw) || 0));
      if (count > 2000) count = 2000;
      if (count === (card.days[idx]?.count ?? 0)) return;
      body.count = count;
    }
    try {
      const { data } = await api.patch(`/calf/cards/${card.id}/day`, body);
      const dkg = data.delta_kg;
      const kgTxt = fmt(Math.round(Math.abs(dkg) * 1000) / 1000);
      if (dkg > 0) toast.success(`${DAY_LABELS[idx]}: ${kgTxt} kg stoktan düşüldü`);
      else if (dkg < 0) toast.success(`${DAY_LABELS[idx]}: ${kgTxt} kg stoğa iade edildi`);
      setDraft((data.days || []).map(d => (isLely ? String(d.kg ?? 0) : String(d.count ?? 0))));
      onChanged(data);
      onMutate && onMutate();
    } catch (err) {
      toast.error(errMsg(err, "Gün güncellenemedi"));
      setDraft((card.days || []).map(d => (isLely ? String(d.kg ?? 0) : String(d.count ?? 0))));
    }
  };

  const saveCalfCount = async () => {
    if (confirmed) return;
    const n = Math.max(0, Math.floor(Number(calfCount) || 0));
    if (n === (card.total_calf_count ?? 0)) return;
    try {
      const { data } = await api.patch(`/calf/cards/${card.id}`, { total_calf_count: n });
      onChanged(data);
      onMutate && onMutate();
      toast.success("Toplam buzağı sayısı güncellendi");
    } catch (err) {
      toast.error(errMsg(err, "Güncellenemedi"));
    }
  };

  const doConfirm = async () => {
    if (!window.confirm(`'${card.stock_name}' · ${card.week_start_date} haftası ONAYLANSIN mı?\nOnaydan sonra kart kilitlenir ve düzenlenemez (kilidi yalnızca yönetici açabilir).`)) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/calf/cards/${card.id}/confirm`);
      onChanged(data);
      onMutate && onMutate();
      toast.success("Hafta onaylandı ve mühürlendi");
    } catch (err) {
      toast.error(errMsg(err, "Onaylanamadı"));
    } finally { setBusy(false); }
  };

  const doReopen = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/calf/cards/${card.id}/reopen`);
      onChanged(data);
      onMutate && onMutate();
      toast.success("Kilit açıldı — kart tekrar düzenlenebilir");
    } catch (err) {
      toast.error(errMsg(err, "Kilit açılamadı"));
    } finally { setBusy(false); }
  };

  const del = async () => {
    if (!window.confirm(`'${card.stock_name}' · ${card.week_start_date} haftası kartı silinsin mi?\nDüşülen ${fmt(card.total_kg)} kg stoğa iade edilecek, finans gideri silinecek.`)) return;
    try {
      await api.delete(`/calf/cards/${card.id}`);
      onRemoved(card.id);
      onMutate && onMutate();
      toast.success("Kart silindi, stok iade edildi");
    } catch (err) {
      toast.error(errMsg(err, "Silme başarısız"));
    }
  };

  const Icon = theme.Icon;

  return (
    <Card testId={`calf-card-${card.id}`} className={`!p-0 overflow-hidden ${confirmed ? "ring-1 ring-[#c2d6c8]" : ""}`}>
      {/* Başlık */}
      <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 border-b" style={{ background: theme.bg, borderColor: theme.border }}>
        <div className="min-w-0">
          <div className="font-semibold text-[#1A1D1A] flex items-center gap-2 flex-wrap">
            <Icon size={16} weight="duotone" style={{ color: theme.fg }} /> {card.stock_name}
            {!isLely && <span className="text-xs font-normal" style={{ color: theme.fg }}>· {fmt(card.kg_per_calf_per_day)} kg/gün</span>}
            {confirmed && (
              <span data-testid={`calf-card-lock-${card.id}`} className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded bg-[#2D4A36] text-white">
                <LockSimple size={11} weight="bold" /> Kapatıldı
              </span>
            )}
          </div>
          <div className="text-xs text-[#5e675e] mt-0.5">
            Hafta: {card.week_start_date}
            {!isLely && ` · ${card.total_calf_days} buzağı-gün`}
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-right">
            <div className="text-sm font-bold tabnum" style={{ color: theme.fg }} data-testid={`calf-card-total-kg-${card.id}`}>{fmt(card.total_kg)} kg</div>
            <div className="text-xs tabnum text-[#C45B45]">{fmt(Math.round(card.total_cost))} ₺</div>
          </div>
          {confirmed ? (
            isManager && (
              <button data-testid={`calf-card-reopen-${card.id}`} disabled={busy} onClick={doReopen} title="Kilidi aç (yönetici)"
                className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs font-medium bg-white text-[#2D4A36] hover:bg-[#EDF3EE] border border-[#c2d6c8]">
                <LockSimpleOpen size={14} weight="bold" /> Kilidi Aç
              </button>
            )
          ) : (
            <button data-testid={`calf-card-del-${card.id}`} onClick={del} title="Kartı sil, stoğu iade et"
              className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium bg-[#FBECEA] text-[#C45B45] hover:bg-[#f5d5ce] border border-[#f2d4cb]">
              <Trash size={14} weight="bold" /> Sil
            </button>
          )}
        </div>
      </div>

      {/* Lely: Toplam Buzağı Sayısı girişi */}
      {isLely && (
        <div className="flex items-center gap-2 px-4 py-2.5 bg-[#F7FAF7] border-b border-[#EEECE5]">
          <span className="text-xs font-semibold text-[#5e675e]">Toplam Buzağı Sayısı:</span>
          <Input type="number" min="0" step="1" className="!w-24 text-center !py-1"
            data-testid={`calf-card-calfcount-${card.id}`}
            value={calfCount} disabled={confirmed}
            onChange={e => setCalfCount(e.target.value)}
            onBlur={saveCalfCount}
            onKeyDown={e => { if (e.key === "Enter") e.target.blur(); }} />
          <span className="text-[11px] text-[#8a938c]">(yalnızca buzağı başı ortalama tüketim hesabı için)</span>
        </div>
      )}

      {/* Kapatılan kart özet şeridi */}
      {confirmed && (
        <div className="grid grid-cols-3 divide-x divide-[#EEECE5] border-b border-[#EEECE5] bg-[#FAFCFA]" data-testid={`calf-card-summary-${card.id}`}>
          <div className="p-3 text-center">
            <div className="text-[10px] uppercase tracking-wider text-[#8a938c] flex items-center justify-center gap-1"><Scales size={12} weight="duotone" /> Toplam Tonaj</div>
            <div className="text-sm font-bold tabnum text-[#2D4A36] mt-0.5">{fmt(card.total_ton)} ton</div>
            <div className="text-[10px] text-[#8a938c]">{fmt(card.total_kg)} kg</div>
          </div>
          <div className="p-3 text-center">
            <div className="text-[10px] uppercase tracking-wider text-[#8a938c] flex items-center justify-center gap-1"><CurrencyCircleDollar size={12} weight="duotone" /> Toplam Maliyet</div>
            <div className="text-sm font-bold tabnum text-[#C45B45] mt-0.5">{fmt(Math.round(card.total_cost))} ₺</div>
          </div>
          <div className="p-3 text-center">
            <div className="text-[10px] uppercase tracking-wider text-[#8a938c] flex items-center justify-center gap-1"><ChartBar size={12} weight="duotone" /> Buzağı Başı Ort.</div>
            <div className="text-sm font-bold tabnum text-[#8B6914] mt-0.5" data-testid={`calf-card-avg-${card.id}`}>{fmt(card.avg_per_calf)} kg</div>
            <div className="text-[10px] text-[#8a938c]">/ buzağı · hafta</div>
          </div>
        </div>
      )}

      {/* Gün kutuları */}
      <div className="grid grid-cols-4 sm:grid-cols-7 gap-2 p-4" data-testid={`calf-card-days-${card.id}`}>
        {DAY_LABELS.map((lbl, i) => {
          const day = (card.days && card.days[i]) || {};
          return (
            <div key={lbl} className={`flex flex-col items-center border rounded-md p-2 ${confirmed ? "bg-[#F2F5F2] border-[#E1E7E1]" : "bg-[#FAFAF7] border-[#EEECE5]"}`}>
              <span className="text-[11px] font-semibold text-[#5e675e]">{lbl}</span>
              <span className="text-[10px] text-[#b3b0a6] mb-1">{(day.date || "").slice(5)}</span>
              <Input type="number" step={isLely ? "0.01" : "1"} min="0"
                className="text-center !px-1"
                disabled={confirmed}
                data-testid={`calf-card-day-${card.id}-${i}`}
                value={draft[i] ?? "0"}
                onChange={e => setDraft(prev => { const a = [...prev]; a[i] = e.target.value; return a; })}
                onBlur={() => saveDay(i)}
                onKeyDown={e => { if (e.key === "Enter") e.target.blur(); }} />
              <span className="text-[10px] mt-1 tabnum" style={{ color: theme.fg }} data-testid={`calf-card-day-kg-${card.id}-${i}`}>
                {fmt(Math.round((day.deducted_kg || 0) * 1000) / 1000)} kg
              </span>
            </div>
          );
        })}
      </div>

      {card.notes && <div className="px-4 pb-2 text-xs text-[#6b746c]">Not: {card.notes}</div>}

      {/* Onayla/Kapat */}
      {!confirmed && (
        <div className="px-4 py-3 border-t border-[#EEECE5] flex justify-end bg-[#FCFCFA]">
          <Button data-testid={`calf-card-confirm-${card.id}`} disabled={busy || card.total_kg <= 0} onClick={doConfirm}>
            <CheckCircle size={15} weight="bold" className="inline mr-1" /> Haftayı Onayla / Kapat
          </Button>
        </div>
      )}
    </Card>
  );
}
